import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Loader,
  styled,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { useEffect, useState } from "react";
import {
  GoogleQuery,
  GoogleAPI,
  IntegrationScope,
  GoogleAuthenticationMethod,
} from "@/Features";
import { GOOGLE_SCOPES } from "@/Features/Integrations/Google/Google.constants";

const Title = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const SubTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const Description = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const ConnectionStatus = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  font-weight: 600;

  width: 500px;

  padding: 15px 20px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const GoogleIntegration = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") ?? "";
  const state = searchParams.get("state") ?? "";
  const [settingUp, setSettingUp] = useState(!!(code && state));
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, refetch } = GoogleQuery.useGetIntegration({
    query: {
      scope: IntegrationScope.TENANT,
    },
    options: {
      enabled: !settingUp,
    },
  });

  const {
    data: authToken,
    isLoading: authTokenLoading,
    isFetched,
  } = GoogleQuery.useSaveOAuth2Credentials(
    {
      code,
      state,
      scope: IntegrationScope.TENANT,
    },
    {
      enabled: !!code && !!state,
    }
  );

  const handleDeleteIntegration = async () => {
    if (!data?.data[0]?.integration_id) return;
    await GoogleAPI.delete({ integration_id: data.data[0].integration_id });
    refetch();
  };

  const handleConnect = async () => {
    const authUrl = await GoogleAPI.getAuthUrl();
    window.location.href = authUrl.data.url;
  };

  useEffect(() => {
    if (!authTokenLoading) {
      setSettingUp(false);
      navigate(location.pathname, { replace: true });
    }
  }, [authTokenLoading]);

  if (isLoading) return null;

  const isGoogleEnabled = !!data?.data[0]?.integration_id;
  const method =
    data?.data[0]?.options?.method ===
    GoogleAuthenticationMethod.SERVICE_ACCOUNT
      ? "Service Account"
      : "OAuth2";

  if (authTokenLoading && settingUp) {
    return (
      <div>
        <Title>Google Integration</Title>
        <Description>
          Connect your Google account to your case to enable the following
          features:
          <ul>
            <li>Import contacts from your Google Workspace account</li>
          </ul>
        </Description>
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <Title>Google Integration</Title>
      <Description>
        Connect your Google Workspace account to your case to enable the
        following features:
        <ul>
          <li>Import contacts from your Google Workspace account</li>
        </ul>
      </Description>
      {!isGoogleEnabled && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            marginTop: 20,
          }}
        >
          {false && (
            <div>
              <Title>Select Authentication Method</Title>
              <Description>
                Select the authentication method you want to use to connect your
                Google account to Monolith.
              </Description>
            </div>
          )}
          {false && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 20,
                alignItems: "flex-start",
                justifyContent: "space-between",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 10,
                padding: 20,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                <SubTitle>Client Credentials/Service Principal</SubTitle>
                <Description>
                  This method is used when you want to connect your Google
                  Workspace tenant using a service account via client
                  credentials. Use this if you do not want to connect Monolith
                  using delegated access.
                </Description>
              </div>
              <Button onClick={() => navigate("service-account-credentials")}>
                Enter Service Account Credentials
              </Button>
            </div>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 20,
              alignItems: "flex-start",
              justifyContent: "space-between",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 10,
              padding: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              <SubTitle>OAuth2</SubTitle>
              <Description>
                Authenticate using a user account. This is will delegate access
                to Monolith via this user account. The account must have
                appropriate permissions and scopes to use the features
                integrated with Monolith.
              </Description>
              <Description>Scopes</Description>
              <Description>
                <ul style={{ margin: 0 }}>
                  {GOOGLE_SCOPES.map((scope) => (
                    <li key={scope}>{scope}</li>
                  ))}
                </ul>
              </Description>
            </div>
            <Button onClick={handleConnect}>Login with OAuth2</Button>
          </div>
        </div>
      )}

      {isGoogleEnabled && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 40,
            marginTop: 20,
          }}
        >
          <ConnectionStatus>
            <div
              style={{
                color: isGoogleEnabled ? "limegreen" : "#BDBDBD",
              }}
            >
              {isGoogleEnabled ? `Connected via ${method}` : "Not Connected"}
            </div>
            <div>
              {isGoogleEnabled ? (
                <Button
                  size="xxs"
                  variant="contained"
                  color="error"
                  onClick={handleDeleteIntegration}
                >
                  Remove Connection
                </Button>
              ) : (
                <Button
                  size="xxs"
                  variant="contained"
                  color="primary"
                  onClick={handleConnect}
                >
                  Connect
                </Button>
              )}
            </div>
          </ConnectionStatus>
        </div>
      )}
    </div>
  );
};
